import type {
    RadialChartGeometryConstantType,
    RadialChartCenterConstantType,
    RadialChartStatusConstantType,
    RadialChartIndexConstantType,
    RadialChartPointConstantType,
    RadialChartGeometry,
    RadialChartBandArc,
    RadialChartCenter,
    RadialChartPoint,
    RadialChartBand,
    RadialChartRing
} from "./type";
import {
    RADIAL_CHART_STACKED_HOLE,
    RADIAL_CHART_GAUGE_START,
    RADIAL_CHART_GAUGE_SPAN,
    RADIAL_CHART_BARS_SPAN,
    RADIAL_CHART_FULL_SPAN,
    RADIAL_CHART_BARS_HOLE
} from "./stylesheet";
import {
    getChartTextWidth
} from "../chart/util";

const getStackedRings = ({
    measures,
    variant,
    count,
    size
}: {
    measures: RadialChartGeometryConstantType["measures"];
    variant: RadialChartGeometryConstantType["variant"];
    count: number;
    size: number;
}) => {
    const {
        minThickness,
        thickness,
        gap
    } = measures;

    const hole = variant === "bars" ? RADIAL_CHART_BARS_HOLE : RADIAL_CHART_STACKED_HOLE;

    const ringThickness = Math.max(minThickness, Math.min(thickness, (size * (1 - hole) + gap) / count - gap));

    const rings = Array.from({
        length: count
    }, (_, index): RadialChartRing => ({
        radius: Math.max(ringThickness / 2, size - ringThickness / 2 - index * (ringThickness + gap)),
        span: variant === "bars" ? RADIAL_CHART_BARS_SPAN : RADIAL_CHART_FULL_SPAN,
        thickness: ringThickness,
        startAngle: 0
    }));

    return {
        pitch: ringThickness + gap,
        rings
    };
};

export const getRadialChartGeometry = ({
    tickWidth,
    hasBands,
    measures,
    variant,
    height,
    width,
    count
}: RadialChartGeometryConstantType): RadialChartGeometry => {
    const {
        labelTypography,
        bandWidth,
        thickness,
        margin,
        lift,
        gap
    } = measures;

    const cx = width / 2;

    if(variant === "gauge") {
        const labelLine = labelTypography.lineHeight;

        const extra = hasBands ? gap + bandWidth : 0;

        const sidePad = hasBands ? gap + tickWidth : 0;

        const topPad = hasBands ? gap + labelLine : 0;

        const bottomPad = thickness / 2 + gap + labelLine;

        const byWidth = width / 2 - margin - thickness / 2 - extra - sidePad - lift / 2;

        const byHeight = height - margin * 2 - topPad - extra - thickness - gap - labelLine;

        const radius = Math.max(0, Math.min(byWidth, byHeight));

        const contentHeight = topPad + extra + thickness / 2 + radius + bottomPad;

        const cy = (height - contentHeight) / 2 + topPad + extra + thickness / 2 + radius;

        return {
            bandRadius: radius + thickness / 2 + gap + bandWidth / 2,
            rings: [
                {
                    startAngle: RADIAL_CHART_GAUGE_START,
                    span: RADIAL_CHART_GAUGE_SPAN,
                    thickness,
                    radius
                }
            ],
            inner: Math.max(0, radius - thickness / 2),
            outer: radius + thickness / 2,
            isLabelVisible: false,
            pitch: 0,
            cx,
            cy
        };
    }

    const cy = height / 2;

    const size = Math.max(0, Math.min(width, height) / 2 - margin - lift / 2);

    if(variant === "progress") {
        const ringThickness = Math.min(thickness, size / 2);

        return {
            inner: Math.max(0, size - ringThickness * 2),
            rings: [
                {
                    radius: size - ringThickness / 2,
                    span: RADIAL_CHART_FULL_SPAN,
                    thickness: ringThickness,
                    startAngle: 0
                }
            ],
            isLabelVisible: false,
            bandRadius: 0,
            outer: size,
            pitch: 0,
            cx,
            cy
        };
    }

    const {
        rings,
        pitch
    } = getStackedRings({
        count: Math.max(1, count),
        measures,
        variant,
        size
    });

    const last = rings[rings.length - 1];

    return {
        isLabelVisible: variant === "bars" && pitch >= labelTypography.lineHeight,
        inner: last ? Math.max(0, last.radius - last.thickness / 2) : 0,
        bandRadius: 0,
        outer: size,
        pitch,
        rings,
        cx,
        cy
    };
};

export const getRadialChartPoint = ({
    geometry,
    radius,
    isRTL,
    angle
}: RadialChartPointConstantType): RadialChartPoint => {
    const ring = radius ?? geometry.rings[0]?.radius ?? 0;

    return {
        x: geometry.cx + (isRTL ? -1 : 1) * ring * Math.sin(angle),
        y: geometry.cy - ring * Math.cos(angle)
    };
};

const getRelativeAngle = (angle: number, start: number): number => {
    const relative = (angle - start) % RADIAL_CHART_FULL_SPAN;

    return relative < 0 ? relative + RADIAL_CHART_FULL_SPAN : relative;
};

export const getRadialChartIndex = ({
    geometry,
    measures,
    variant,
    isRTL,
    x,
    y
}: RadialChartIndexConstantType): number | undefined => {
    const dx = x - geometry.cx;

    const dy = y - geometry.cy;

    const distance = Math.sqrt(dx * dx + dy * dy);

    const angle = Math.atan2(isRTL ? -dx : dx, -dy);

    const isInSpan = (ring: RadialChartRing): boolean => {
        if(ring.span >= RADIAL_CHART_FULL_SPAN) {
            return true;
        }

        const relative = getRelativeAngle(angle, ring.startAngle);

        const tolerance = ring.thickness / 2 / Math.max(ring.radius, 1);

        return relative <= ring.span + tolerance || relative >= RADIAL_CHART_FULL_SPAN - tolerance;
    };

    if(variant === "progress" || variant === "gauge") {
        const ring = geometry.rings[0];

        if(!ring) {
            return undefined;
        }

        return distance <= ring.radius + ring.thickness / 2 + measures.gap && isInSpan(ring) ? 0 : undefined;
    }

    const isStartSide = (isRTL ? dx > 0 : dx < 0) && dy < 0;

    for(let index = 0; index < geometry.rings.length; index += 1) {
        const ring = geometry.rings[index];

        if(!ring) {
            continue;
        }

        if(Math.abs(distance - ring.radius) <= (ring.thickness + measures.gap) / 2 && isInSpan(ring)) {
            return index;
        }

        if(variant === "bars" && geometry.isLabelVisible && isStartSide && Math.abs(y - (geometry.cy - ring.radius)) <= geometry.pitch / 2) {
            return index;
        }
    }

    return undefined;
};

export const getRadialChartCenter = ({
    geometry,
    measures,
    variant,
    title,
    value
}: RadialChartCenterConstantType): RadialChartCenter | undefined => {
    if(geometry.inner <= 0) {
        return undefined;
    }

    const {
        labelTypography,
        valueSteps,
        clearance,
        gap
    } = measures;

    const options = title ? [
        true,
        false
    ] : [
        false
    ];

    for(const isTitleVisible of options) {
        for(const step of valueSteps) {
            const stackHeight = step.lineHeight + (isTitleVisible ? labelTypography.lineHeight : 0);

            const isGauge = variant === "gauge";

            const farthest = isGauge ? stackHeight + gap : stackHeight / 2;

            const chord = 2 * Math.sqrt(Math.max(0, geometry.inner * geometry.inner - farthest * farthest));

            const available = chord - clearance * 2;

            const isValueFits = getChartTextWidth({
                fontSize: step.fontSize,
                text: value
            }) <= available;

            const isTitleFits = !isTitleVisible || getChartTextWidth({
                fontSize: labelTypography.fontSize,
                text: title ?? ""
            }) <= available;

            if(isValueFits && isTitleFits) {
                return {
                    top: isGauge ? geometry.cy - gap - stackHeight : geometry.cy - stackHeight / 2,
                    titleTypography: isTitleVisible ? labelTypography : undefined,
                    valueTypography: step,
                    isTitleVisible
                };
            }
        }
    }

    return undefined;
};

export const getRadialChartBands = ({
    bands,
    max
}: {
    bands: Array<RadialChartBand>;
    max: number;
}): Array<RadialChartBandArc> => {
    const sorted = [
        ...bands
    ].sort((first, second) => first.to - second.to);

    let previous = 0;

    return sorted.reduce<Array<RadialChartBandArc>>((arcs, band) => {
        const end = Math.min(max, Math.max(previous, band.to));

        if(end > previous && max > 0) {
            arcs.push({
                startRatio: previous / max,
                endRatio: end / max,
                status: band.status
            });
        }

        previous = end;

        return arcs;
    }, []);
};

export const getRadialChartStatus = ({
    bands,
    value
}: RadialChartStatusConstantType): RadialChartBand | undefined => {
    if(!bands?.length) {
        return undefined;
    }

    const sorted = [
        ...bands
    ].sort((first, second) => first.to - second.to);

    return sorted.find((band) => value <= band.to) ?? sorted[sorted.length - 1];
};
